import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { renderBlock } from "@/components/legal/LegalDocView";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FACE_DATA_PRODUCTS,
  getFaceDataPolicyCopy,
  type FaceDataProduct,
  type ProductPolicy,
} from "@/content/faceDataPolicy";

/**
 * Photohawk's face data policy at /face-data-policy (owner, 20 October 2026): one official page, a short intro and a
 * separate, clearly labelled policy for each product in its own tab (#studio, #family). Both tabs are in the
 * server-rendered HTML (the inactive one hidden), so every word is indexable and works without JavaScript's tab
 * switch. Content: src/content/faceDataPolicy (en, es, pt).
 */
const isProduct = (v: string): v is FaceDataProduct => (FACE_DATA_PRODUCTS as readonly string[]).includes(v);

const ProductSection = ({ policy }: { policy: ProductPolicy }) => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-display font-bold mb-3">{policy.heading}</h2>
      <p className="text-muted-foreground leading-relaxed">{policy.summary}</p>
    </div>
    {policy.sections.map((section, s) => (
      <section key={s} className="space-y-3">
        <h3 className="text-lg font-semibold">{section.heading}</h3>
        {section.blocks.map(renderBlock)}
      </section>
    ))}
    <div className="border-t border-border pt-6">{renderBlock({ type: "link", ...policy.full }, 0)}</div>
  </div>
);

const FaceDataPolicy = () => {
  const { i18n } = useTranslation();
  const copy = getFaceDataPolicyCopy(i18n.language);
  const [tab, setTab] = useState<FaceDataProduct>("studio");

  // The tab follows the URL's #studio / #family (footer links, the app's link, a shared URL). Read after mount so
  // the server render and hydration agree (the server never sees the fragment).
  useEffect(() => {
    const fromHash = () => {
      const h = window.location.hash.replace(/^#/, "");
      if (isProduct(h)) setTab(h);
    };
    fromHash();
    window.addEventListener("hashchange", fromHash);
    return () => window.removeEventListener("hashchange", fromHash);
  }, []);

  const choose = (v: string) => {
    if (!isProduct(v)) return;
    setTab(v);
    try {
      window.history.replaceState(window.history.state, "", `#${v}`);
    } catch {
      /* ignore */
    }
  };

  const meta = copy.meta.replace("{version}", copy.version).replace("{date}", copy.effectiveDate);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{copy.seo.title}</title>
        <meta name="description" content={copy.seo.description} />
      </Helmet>
      <AppHeader variant="marketing" />
      <main className="container mx-auto px-4 pt-28 pb-20 max-w-3xl" data-testid="face-data-policy">
        <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">{copy.title}</h1>
        <p className="text-sm text-muted-foreground mb-8">{meta}</p>
        <div className="space-y-4 border-l-2 border-border pl-4 mb-10">
          {copy.intro.map((p, i) => (
            <p key={i} className="text-muted-foreground leading-relaxed">
              {p}
            </p>
          ))}
        </div>

        <Tabs value={tab} onValueChange={choose}>
          <TabsList aria-label={copy.tabsLabel} className="mb-8 grid h-auto w-full grid-cols-2">
            {FACE_DATA_PRODUCTS.map((id) => (
              <TabsTrigger key={id} value={id} className="whitespace-normal py-2">
                {copy[id].tab}
              </TabsTrigger>
            ))}
          </TabsList>
          {FACE_DATA_PRODUCTS.map((id) => (
            <TabsContent key={id} value={id} forceMount data-testid={`policy-${id}`} className="mt-0 data-[state=inactive]:hidden">
              <ProductSection policy={copy[id]} />
            </TabsContent>
          ))}
        </Tabs>

        <section className="mt-12 border-t border-border pt-6 space-y-2" data-testid="face-data-policy-changes">
          <h2 className="text-lg font-semibold">{copy.changelog.title}</h2>
          <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
            {copy.changelog.items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FaceDataPolicy;
